import { workAreaDisplayName } from '@featit/shared/work-area-seed-names';
import { asIfAccessToken } from '@/features/as-if/asIfView';
import { deviceLocal, deviceSession } from '@/privacy/deviceStorage';
import { makeObservable, observable, computed, action, runInAction } from 'mobx';
import { workAreaPermalinkRef, workAreaRouteRef } from '@featit/shared/addressing/workAreaRouteRef';
import {
  type IProject,
  type IWorkAreaType,
  type IWorkAreaTypeDefaults,
} from '@featit/shared/types/index';
import {
  policiesRevisionOf,
  workAreaDetailsConflictOf,
  workAreaPoliciesConflictOf,
} from '@featit/shared/work-area-policies-revision';
import { projectApi, projectApiFor } from '@/services/ProjectApi';
import { hasStoredAuthSession } from '@/services/ApiService';
import {
  fetchWorkAreaEffectiveRules,
  type IWorkAreaEffectiveRules,
} from '@/services/WorkAreaRulesApi';
import {
  WORK_AREA_DATA_SOURCE_AVAILABILITY,
  type IWorkAreaDataSourceDescriptor,
  type ICreateCloudWorkAreaInput,
  type IUpdateWorkAreaInput,
  type WorkAreaDataSourceRegistry,
  workAreaDataSourceRegistry,
} from '@/data-sources/WorkAreaDataSourceRegistry';
import {
  failureToSay,
  type HeldSentenceType,
  pageWords,
  sayFailure,
} from '@/services/actionFailure';
import { describeLoadFailure, type ILoadFailure } from '@/services/loadFailure';
import { dropOwnerRuleDatabases, dropOwnerRuleStoresOfThisDevice } from '@/privacy/ownerRule';
import type { MyWorkAreasListener } from '@/local-sync/myWorkAreas';
import { settleWithin } from '@/utils/accessReadDeadline';
import { reportWorkAreaDiscovery, workAreaDiscoveryFailed } from '@/utils/workAreaPermissionGate';

/**
 * The stale-save refusals (`staleSettingsRefusal.ts`, with the English words of `staleRefusals`,
 * about 4 KB with the module) are built only on a refused save, so the launch entry does not carry
 * them (`check-first-screen-chunks.mjs`, `StaleSettingsRefusal`); the first refusal loads them.
 */
const staleRefusals = () => import('@/services/staleSettingsRefusal');

/**
 * I-1525 (`docs/concept-mobile-app.md` AC-19.19): what keeps the work-area list current while the
 * window is open — the person's `myWorkAreasChanges` stream, and the window coming back into focus.
 * Injected, so a store built in a test follows nothing unless it is handed these.
 */
export interface IWorkAreaListLive {
  subscribe: (listener: MyWorkAreasListener) => () => void;
  /** Opens the stream once a read has proved the tab has a session. */
  wake?: () => void;
  /**
   * The app coming back to the foreground, which the iPhone and iPad shell says itself: a web view
   * that was suspended may have slept through its socket and through the window's own events.
   */
  onForeground?: (listener: () => void) => () => void;
  /** Where focus and visibility are heard; the window, unless a test supplies its own. */
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'> & {
    document?: Pick<Document, 'visibilityState'>;
  };
}

/**
 * A window coming back into focus re-reads the list, but not more often than this: switching between
 * two windows a few times is one re-read, not one each.
 */
export const FOCUS_REFRESH_MIN_MS = 5_000;

/**
 * Where the selected work area is remembered.
 *
 * ⭐ **Two stores, and they answer two different questions.** `sessionStorage` is *this window's*
 * selection, so two tabs can sit in two work areas without fighting. `localStorage` is the last work
 * area this browser was in, and it is the one that survives a restart.
 *
 * ⛔ **A restart must not move the reader.** The selection lived in `sessionStorage` alone, which
 * dies with the window — so the desktop app, where every launch is a new session, opened on
 * `projects[0]` every single time and silently moved whoever had been working somewhere else. Jay,
 * 2026-09-14: *"why does the selected project always switch after that restart?"*.
 */
const SELECTED_KEY = 'featit:selectedProjectId';

/**
 * The selected work area's **name**, remembered beside its id and in the same two stores.
 *
 * ⛔ An id is not a label. `selectedProject` is `projectMap[selectedProjectId]`, and `projectMap` is
 * empty until `fetchProjects` has answered — so every reader that needed the work area's *name* got
 * `undefined` for the first paint of every load, and for the whole session whenever that fetch
 * fails. The browser tab was one of them: with no name to show it fell back to the product's own,
 * and told a reader sitting in Evorix that they were looking at the installation (I-0611).
 *
 * The id was already remembered for precisely this window. The label is the same fact about the same
 * moment, so it is remembered the same way.
 */
const SELECTED_NAME_KEY = 'featit:selectedWorkAreaName';

/**
 * I-1423: the addresses the remembered name belongs to, its route reference and its permalink one,
 * one per line. Before the work areas answer, the top row's scope names the work area only at one of
 * these: the address on screen may be another work area's, and a name it then replaced would be a
 * wrong name and a moving ▾ both.
 */
const SELECTED_REFS_KEY = 'featit:selectedWorkAreaRefs';

/**
 * I-2503: `1` when the remembered work area's name had the agency's logo in front of it on the phone's
 * top row. A returning guest's name is then held from the first frame, before the work areas answer,
 * so the logo arriving after them moves no name already drawn. Cleared with the name, and whenever
 * another work area is remembered.
 */
const SELECTED_MARK_KEY = 'featit:selectedWorkAreaHadMark';

/** This window's selection, or the last one this browser used. */
function storedSelection(): string | null {
  return deviceSession.getItem(SELECTED_KEY) ?? deviceLocal.getItem(SELECTED_KEY);
}

/** The remembered selection's name, read the same two ways as the selection itself. */
function storedSelectionName(): string | null {
  return deviceSession.getItem(SELECTED_NAME_KEY) ?? deviceLocal.getItem(SELECTED_NAME_KEY);
}

/** Whether the remembered name had the agency's logo, read the same two ways as the name. */
function storedSelectionHadMark(): boolean {
  return (
    (deviceSession.getItem(SELECTED_MARK_KEY) ?? deviceLocal.getItem(SELECTED_MARK_KEY)) === '1'
  );
}

/** The remembered name's addresses, read the same two ways as the name. */
function storedSelectionRefs(): string[] {
  const stored = deviceSession.getItem(SELECTED_REFS_KEY) ?? deviceLocal.getItem(SELECTED_REFS_KEY);
  return stored ? stored.split('\n').filter(Boolean) : [];
}

/**
 * Whether "All work areas" is the scope, remembered the same two ways as the selection (I-0405).
 *
 * ⭐ A key of its own rather than a sentinel in `SELECTED_KEY`, so the last concrete work area is kept
 * while All is chosen — a view that needs one work area can offer it back, and choosing one again does
 * not start from `projects[0]`.
 *
 * F-0383, §10.9 NV-7: a namespace scope is the same key, holding `namespace:<id>` — the same
 * `viewScopeKey` convention `@featit/shared`'s `view-scope.ts` uses for a stored preference, so a
 * namespace is told apart from a work area id by a prefix rather than by a second key to keep in
 * sync with this one. Keyed by id, never the namespace's path: a changed handle (ADR-111) must not
 * detach the reader's open scope from the namespace that still exists under it.
 */
const SCOPE_KEY = 'featit:workAreaScope';
/**
 * I-1118: the work area a person last opened (a pick or a link into one). Kept apart from
 * `SELECTED_KEY`, which a load's fallback writes too: a fallback is not the person remembering.
 */
const LAST_OPENED_KEY = 'featit:lastOpenedWorkAreaId';
const ALL_WORK_AREAS = 'all';
export const NAMESPACE_SCOPE_PREFIX = 'namespace:';
/**
 * F-0530 (AC-19.1): the app's one scope on the phone, the person's choice. It is `all` or a work
 * area's id. Kept apart from `SCOPE_KEY`, which a link into a work area and the I-1118 redirect
 * write too: an address that implies a scope is not the person choosing one. Every tab's web view
 * shares this storage, so a tab that loads later opens on it and a relaunch keeps it.
 */
const APP_SCOPE_KEY = 'featit:appScope';
/** F-0530 (AC-19.3): the work areas opened most recently, newest first, for the switcher. */
const RECENT_KEY = 'featit:recentWorkAreaIds';
/**
 * I-1213 (AC-3): whose places the keys above are. A person's remembered work area, scope, recent
 * work areas and last-opened one are theirs alone: the next person to sign in on this browser or in
 * this app (the web's storage is per deployment, not per person) starts on their own, never on a
 * work area of the last person's (`adoptPerson`).
 */
const PLACE_OWNER_KEY = 'featit:placeOwner';
const RECENT_LIMIT = 5;

/**
 * F-0530 (AC-19.8): the app scope as every web view shares it right now — the one value a badge is
 * counted for, whichever tab's web view posts it, even one whose own copy has fallen behind.
 */
export function sharedAppScope(): string | null {
  try {
    return deviceLocal.getItem(APP_SCOPE_KEY);
  } catch {
    return null;
  }
}

/** The stored app scope, or the person's last remembered scope from before it existed. */
function storedAppScope(): string | null {
  const stored = deviceLocal.getItem(APP_SCOPE_KEY);
  if (stored) return stored;
  const legacy = deviceLocal.getItem(SCOPE_KEY);
  return legacy && !legacy.startsWith(NAMESPACE_SCOPE_PREFIX) ? legacy : null;
}

function storedRecentIds(): string[] {
  try {
    const parsed: unknown = JSON.parse(deviceLocal.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string').slice(0, RECENT_LIMIT)
      : [];
  } catch {
    return [];
  }
}

function storedScope(): string | null {
  return deviceSession.getItem(SCOPE_KEY) ?? deviceLocal.getItem(SCOPE_KEY);
}

function storedAllWorkAreas(): boolean {
  const session = deviceSession.getItem(SCOPE_KEY);
  if (session !== null) return session === ALL_WORK_AREAS;
  return deviceLocal.getItem(SCOPE_KEY) === ALL_WORK_AREAS;
}

/** The namespace id `SCOPE_KEY` holds, or `null` when the scope is All work areas or one work area. */
function storedNamespaceScope(): string | null {
  const scope = storedScope();
  return scope?.startsWith(NAMESPACE_SCOPE_PREFIX)
    ? scope.slice(NAMESPACE_SCOPE_PREFIX.length)
    : null;
}

export class ProjectStore {
  projectMap: Record<string, IProject> = {};
  projectIds: string[] = [];
  workAreaTypes: IWorkAreaType[] = [];
  dataSourceMap: Record<string, IWorkAreaDataSourceDescriptor> = {};
  unavailableDataSources: IWorkAreaDataSourceDescriptor[] = [];
  /**
   * Each work area's **effective** policy, as the server will evaluate it, keyed by work-area id.
   *
   * ⛔ Kept apart from `projectMap[id].policies`, which is the work area's *stored* blob and is
   * empty on every work area that has never been given a template. A screen that states a rule
   * before the act has to read this one; reading the stored blob is `I-0541`, where the close
   * dialog asserted that closing needed nothing while the server refused it.
   */
  effectiveRulesMap: Record<string, IWorkAreaEffectiveRules> = {};
  /**
   * Work areas whose effective rules were asked for and could not be read (`I-0541`). Absent from
   * both this and `effectiveRulesMap` means the answer is still on its way.
   *
   * ⛔ Kept so a screen can SAY the rules failed, with a retry. Leaving the entry absent and saying
   * nothing made a failed read indistinguishable from a slow one, and the close dialog waited on it
   * forever with Submit disabled and no reason given.
   */
  effectiveRulesFailed: Record<string, boolean> = {};
  /**
   * T-0556, SR-8: the last failure of each work area's rules read, categorised for a
   * `LoadFailureBanner`. Kept while a retry is in flight, so the banner shows it is reading rather
   * than vanishing into a guess, and removed when a read succeeds.
   */
  effectiveRulesFailure: Record<string, ILoadFailure> = {};
  selectedProjectId: string | null = null;
  /** The selected work area's name as this browser last knew it — see {@link SELECTED_NAME_KEY}. */
  rememberedWorkAreaName: string | null = null;
  /** The addresses that name holds for — see {@link SELECTED_REFS_KEY}. */
  rememberedWorkAreaRefs: string[] = [];
  /** The remembered name had the agency's logo in front of it — see {@link SELECTED_MARK_KEY}. */
  rememberedWorkAreaHadMark = false;
  /**
   * "All work areas" is the scope (I-0405). Until this existed the scope was only ever a URL family
   * (`/work-areas/*`), so choosing it sent the reader to that family's Home, and leaving it — to
   * Agents, say — read the last selection back, which is how it "reset" to the first work area.
   */
  allWorkAreas = false;
  /**
   * I-1118: the work area this browser last opened — a person's own pick or a link into one, never
   * the fallback a load picks when nothing is remembered. Under All, Items and Roadmap open it.
   */
  lastOpenedWorkAreaId: string | null = null;
  /**
   * I-1992 (AC-19.21.2): the scope this person chose on this device and kept for the next window —
   * a work area, All work areas or a namespace — or `null`. Never an All an address implied (I-1118)
   * and never the fallback a load takes.
   */
  rememberedScope: string | null = null;
  /**
   * One namespace's whole subtree is the scope (F-0383, §10.9) — a third value beside a single
   * work area and All work areas, never set at the same time as either. `null` outside that scope.
   */
  selectedNamespaceId: string | null = null;
  /**
   * F-0530 (AC-19.1): the app's one scope on the phone, `all` or a work area's id, or `null` before
   * anything was chosen. Only a choice writes it ({@link chooseAppScope}).
   */
  appScope: string | null = null;
  /**
   * I-1213: the app scope named a work area the list no longer holds (deleted, or access removed),
   * and the name it was last seen under when this window knew it. Set by {@link fetchProjects} on an
   * answer, cleared by {@link acknowledgeUnavailableAppScope}; the app falls back to All work areas
   * and says so once (`useAppScope`).
   */
  unavailableAppScope: { id: string; name: string | null } | null = null;
  /**
   * I-1876 (MWI-27): the one work area this window was scoped to is not in the list the server
   * answered (deleted, or access removed), and the name it was last seen under. Set by
   * {@link fetchProjects}, which falls the scope back to All work areas; cleared by
   * {@link acknowledgeUnavailableWorkArea} once the shell has said so.
   */
  unavailableWorkArea: { id: string; name: string | null } | null = null;
  /** F-0530 (AC-19.3): the work areas opened most recently, newest first. */
  recentWorkAreaIds: string[] = [];
  /**
   * I-1472: this window's fleet read found agent sessions outside the person's work areas (a folder
   * no work area declares), so All work areas is a real choice even with one work area. Set by the
   * Agents view, the one reader that learns it; false until it says so.
   */
  sessionsOutsideWorkAreas = false;
  loading = false;
  /**
   * A discovery has succeeded at least once, so this store knows what exists.
   *
   * `loading` cannot answer that. It goes true on every refetch, and callers that read it to decide
   * what to show a reader end up reporting a background refresh as an unknown — which on a desktop
   * instance with no reachable cloud service is very nearly always (I-0173). This never goes back
   * to false for the same person: once we have been told what exists, a later fetch can only replace
   * that answer. Another person signing in withdraws it (`adoptPerson`, I-2104).
   */
  hasWorkAreaAnswer = false;
  /** T-0471: what `error` says, kept, so it is written in the page's language when read. */
  heldError: HeldSentenceType | null = null;
  get error(): string | null {
    return sayFailure(pageWords(), this.heldError);
  }
  set error(value: HeldSentenceType | null) {
    this.heldError = value;
  }
  /**
   * The last discovery's failure, categorised for `LoadFailureBanner` — `error` is the same failure
   * as a sentence for the switcher. A surface that joins its rows to the work-area list (decisions
   * and tasks grouped by work area, I-1077) needs to say "the work areas did not load, Try again"
   * rather than draw an empty list, and the banner wants the category, never the rejection's text.
   */
  failure: ILoadFailure | null = null;
  /** I-1525: the background re-read under way, and whether another was asked for meanwhile. */
  private refreshing: Promise<void> | null = null;
  private refreshAgain = false;
  private lastAnsweredAt = 0;
  private readonly wakeLive: (() => void) | null;

  constructor(
    private readonly dataSourceRegistry: WorkAreaDataSourceRegistry = workAreaDataSourceRegistry,
    live?: IWorkAreaListLive,
  ) {
    this.wakeLive = live?.wake ?? null;
    makeObservable(this, {
      projectMap: observable,
      projectIds: observable,
      workAreaTypes: observable,
      dataSourceMap: observable,
      unavailableDataSources: observable,
      effectiveRulesMap: observable,
      effectiveRulesFailed: observable,
      effectiveRulesFailure: observable,
      fetchEffectiveRules: action,
      selectedProjectId: observable,
      rememberedWorkAreaName: observable,
      rememberedWorkAreaRefs: observable.ref,
      rememberedWorkAreaHadMark: observable,
      selectedWorkAreaName: computed,
      allWorkAreas: observable,
      lastOpenedWorkAreaId: observable,
      rememberedScope: observable,
      hasRememberedPlace: computed,
      selectedNamespaceId: observable,
      scopeWorkAreaId: computed,
      selectNamespace: action,
      appScope: observable,
      recentWorkAreaIds: observable,
      sessionsOutsideWorkAreas: observable,
      noteSessionsOutsideWorkAreas: action,
      chooseAppScope: action,
      unavailableAppScope: observable.ref,
      acknowledgeUnavailableAppScope: action,
      unavailableWorkArea: observable.ref,
      acknowledgeUnavailableWorkArea: action,
      loading: observable,
      hasWorkAreaAnswer: observable,
      heldError: observable.ref,
      error: computed,
      failure: observable.ref,
      projects: computed,
      selectedProject: computed,
      fetchProjects: action,
      refreshWorkAreas: action,
      fetchWorkAreaTypes: action,
      selectProject: action,
      selectAllWorkAreas: action,
      enterWorkArea: action,
      clearProjectSelection: action,
      forgetWorkArea: action,
      adoptPerson: action,
      hydrateSelection: action,
      createProject: action,
      updateProject: action,
      setAgentActivations: action,
      createWorkAreaType: action,
      updateWorkAreaType: action,
      removeWorkAreaType: action,
    });

    // ⭐ This window first, the last run second. A tab that has chosen keeps its choice; a window
    // that has not chosen yet opens where this browser last was, rather than on whichever work area
    // happens to sort first.
    this.selectedProjectId = storedSelection();
    this.rememberedWorkAreaName = storedSelectionName();
    this.rememberedWorkAreaRefs = storedSelectionRefs();
    this.rememberedWorkAreaHadMark = storedSelectionHadMark();
    this.allWorkAreas = storedAllWorkAreas();
    this.selectedNamespaceId = storedNamespaceScope();
    this.lastOpenedWorkAreaId = deviceLocal.getItem(LAST_OPENED_KEY);
    this.rememberedScope = deviceLocal.getItem(SCOPE_KEY);
    this.appScope = storedAppScope();
    this.recentWorkAreaIds = storedRecentIds();
    /*
      F-0530 (AC-19.1): in the app the person's chosen scope is what a fresh web view opens on — a
      tab loading after the pick, or reloaded after a memory release — whatever this window's own
      earlier selection said. Only the app writes `APP_SCOPE_KEY`, so a browser keeps a tab's own
      selection as before.
    */
    const chosen = deviceLocal.getItem(APP_SCOPE_KEY);
    if (chosen === ALL_WORK_AREAS) {
      this.allWorkAreas = true;
      this.selectedNamespaceId = null;
    } else if (chosen && !chosen.startsWith(NAMESPACE_SCOPE_PREFIX)) {
      this.allWorkAreas = false;
      this.selectedNamespaceId = null;
      this.selectedProjectId = chosen;
    }
    if (live) this.followLive(live);
  }

  /**
   * I-1525 (AC-19.19): the list follows the person's work areas while the window is open. A ring of
   * `myWorkAreasChanges` re-reads it, and so does the stream coming back after a gap (a ring may have
   * been missed) and the window coming back into focus (the socket may have slept with the machine).
   * For the life of the page: the store is the app's one.
   */
  private followLive(live: IWorkAreaListLive): void {
    const target = live.target ?? (typeof window === 'undefined' ? undefined : window);
    if (!target) return;
    live.subscribe({
      onChange: () => this.refreshWorkAreas(),
      onResume: () => this.refreshWorkAreas(),
    });
    const onFocus = () => {
      // Only a list this window has had: before the first answer, the load itself is under way, and
      // a signed-out window has nothing to re-read.
      if (!this.hasWorkAreaAnswer) return;
      if (target.document && target.document.visibilityState === 'hidden') return;
      if (Date.now() - this.lastAnsweredAt < FOCUS_REFRESH_MIN_MS) return;
      this.refreshWorkAreas();
    };
    target.addEventListener('focus', onFocus);
    target.addEventListener('visibilitychange', onFocus);
    live.onForeground?.(onFocus);
  }

  /**
   * Re-read the work areas in the background (I-1525): the list on screen stays, with no loading
   * state, until the answer replaces it. Calls that arrive while one is under way become one more
   * read after it, so a burst of rings is two reads at most and the last one sees the last change.
   *
   * I-1589 (DM-18): never without a stored session. A signed-out web view hears the same focus and
   * foreground signals, and each re-read it sent was a 401 on `GET /api/work-areas`.
   */
  refreshWorkAreas(): void {
    if (!hasStoredAuthSession()) return;
    if (this.refreshing) {
      this.refreshAgain = true;
      return;
    }
    this.refreshing = this.fetchProjects({ background: true }).finally(() => {
      this.refreshing = null;
      if (!this.refreshAgain) return;
      this.refreshAgain = false;
      this.refreshWorkAreas();
    });
  }

  /**
   * The selected work area's name — **before the work areas have arrived as well as after**.
   *
   * ⛔ Read this, not `selectedProject?.name`, wherever a *label* is wanted rather than the work
   * area itself. `selectedProject` resolves an id against `projectMap`, which is empty on the first
   * paint of every load and stays empty for the whole session when discovery fails; a label taken
   * from it is `undefined` in exactly the window a reader is staring at the screen (I-0611).
   *
   * The live name wins the moment it exists, so a work area renamed in another window is stale here
   * only until the work areas arrive — which is the window this getter is for.
   */
  /**
   * I-1992 (AC-19.21.2): whether this person has a place of their own on this device — a work area
   * they opened, a scope they chose here or in the app; the selection a load falls back to is not a
   * place of theirs. Since I-2216 it no longer decides where the app's home lands: that is All work
   * areas whatever is remembered (`appHomeTargetFor`).
   */
  get hasRememberedPlace(): boolean {
    return (
      this.lastOpenedWorkAreaId !== null || this.appScope !== null || this.rememberedScope !== null
    );
  }

  get selectedWorkAreaName(): string | undefined {
    // F-0737 PW-R3: a Personal work area in the page's language until its person renames it.
    const selected = this.selectedProject;
    return (
      (selected ? workAreaDisplayName(selected) : null) ?? this.rememberedWorkAreaName ?? undefined
    );
  }

  /**
   * I-1423: the remembered name, when the address on screen (`acme/tracker`) is one of the work area
   * it was remembered for; `undefined` otherwise. For the moment before the work areas answer.
   */
  rememberedNameAt(reference: string | null): string | undefined {
    if (!reference || !this.rememberedWorkAreaName) return undefined;
    return this.rememberedWorkAreaRefs.includes(reference)
      ? this.rememberedWorkAreaName
      : undefined;
  }

  /** I-2503: whether the name remembered at `reference` had the agency's logo in front of it. */
  rememberedMarkAt(reference: string | null): boolean {
    return this.rememberedWorkAreaHadMark && this.rememberedNameAt(reference) !== undefined;
  }

  /**
   * I-2503: record whether the selected work area's name had the agency's logo in front of it, once
   * the logo has settled on the phone's top row.
   */
  rememberWorkAreaMark(hadMark: boolean): void {
    this.rememberedWorkAreaHadMark = hadMark;
    if (hadMark) {
      deviceSession.setItem(SELECTED_MARK_KEY, '1');
      deviceLocal.setItem(SELECTED_MARK_KEY, '1');
    } else {
      deviceSession.removeItem(SELECTED_MARK_KEY);
      deviceLocal.removeItem(SELECTED_MARK_KEY);
    }
  }

  /**
   * The work area the screen is scoped to: `null` when All work areas or one namespace is chosen.
   *
   * ⛔ Read this, not `selectedProjectId`, wherever "all" or "a namespace" is a meaningful answer.
   * `selectedProjectId` keeps the last concrete work area on purpose, so it is never null while any
   * exist (F-0383, §10.9: a namespace scope keeps it too, the same reason All work areas does).
   */
  get scopeWorkAreaId(): string | null {
    return this.allWorkAreas || this.selectedNamespaceId ? null : this.selectedProjectId;
  }

  /**
   * Re-read the selection after something replaced the session namespace under this store.
   *
   * ⭐ The desktop shell switches tabs by swapping `sessionStorage` for the incoming tab's snapshot
   * (`tabSessionState.ts`) — the browser does the same thing by giving each tab its own namespace.
   * A store built once at boot cannot notice that, so the shell tells it.
   *
   * ⛔ **A tab that carries no work area keeps the one on screen, and takes ownership of it.** The
   * alternative — falling back to null — would drop a reader into "all work areas" on a tab switch,
   * which is a place they never asked to be. Writing it back means the tab answers for itself from
   * then on.
   */
  hydrateSelection(): void {
    const stored = storedSelection();
    this.allWorkAreas = storedAllWorkAreas();
    this.selectedNamespaceId = storedNamespaceScope();
    if (stored) {
      this.selectedProjectId = stored;
      return;
    }
    if (this.selectedProjectId) this.selectProject(this.selectedProjectId);
  }

  get projects(): IProject[] {
    return this.projectIds.map((id) => this.projectMap[id]).filter(Boolean);
  }

  get selectedProject(): IProject | undefined {
    if (!this.selectedProjectId) return undefined;
    return this.projectMap[this.selectedProjectId];
  }

  /**
   * Read the work areas. `background` (I-1525) keeps the screen as it is while the read is under way —
   * no loading state, the controls that wait on one stay enabled — for a re-read nobody asked for.
   */
  async fetchProjects(options: { background?: boolean } = {}) {
    if (!options.background) {
      this.loading = true;
      this.error = null;
      this.failure = null;
    }
    // KD-17a (I-1708): a re-read after a failed discovery is a retry in flight, so a gate waiting on
    // a work area still to arrive checks again rather than repeating the failure it is retrying.
    // KD-17c (I-1702): only one somebody asked for. A background re-read keeps the refusal until it
    // answers, so a discovery that never answers is not "checking again" for 20 s at every focus.
    const retry = () => void this.fetchProjects();
    if (workAreaDiscoveryFailed() && !options.background) {
      reportWorkAreaDiscovery({ failed: true, retrying: true, retry });
    }
    try {
      // I-1702: a discovery that never answers fails after the app's request timeout, so the gates
      // waiting on a work area still to arrive are not pending for good.
      const descriptors = await settleWithin(this.dataSourceRegistry.discover());
      const availableDescriptors = descriptors.filter(
        (descriptor) => descriptor.availability === WORK_AREA_DATA_SOURCE_AVAILABILITY.ready,
      );
      const projects = availableDescriptors.map((descriptor) => descriptor.project);
      runInAction(() => {
        this.projectMap = {};
        projects.forEach((p) => {
          this.projectMap[p.id] = p;
        });
        this.projectIds = projects.map((p) => p.id);
        this.dataSourceMap = Object.fromEntries(
          availableDescriptors.map((descriptor) => [descriptor.project.id, descriptor]),
        );
        this.unavailableDataSources = descriptors.filter(
          (descriptor) => descriptor.availability !== WORK_AREA_DATA_SOURCE_AVAILABILITY.ready,
        );
        // I-1213: read before the selection's own fallback below renames what is remembered.
        const scope = this.appScope;
        // I-2484: a tab holding a view lists the view's one work area; the person's own, missing
        // from that list, is not gone, so neither notice is said there.
        const inAView = asIfAccessToken() !== null;
        if (
          !inAView &&
          scope &&
          scope !== ALL_WORK_AREAS &&
          !scope.startsWith(NAMESPACE_SCOPE_PREFIX) &&
          !projects.some((project) => project.id === scope)
        ) {
          this.unavailableAppScope = {
            id: scope,
            name: scope === this.selectedProjectId ? this.rememberedWorkAreaName : null,
          };
        }
        const selectedProjectExists =
          this.selectedProjectId &&
          projects.some((project) => project.id === this.selectedProjectId);
        // I-1876: read before the fallback below renames what is remembered. Under All or a
        // namespace the reader was not in that one work area, so there is nothing to say.
        // The captain's ruling (2026-10-22): the scope falls back to All work areas, as the iPhone
        // app's does (I-1213) — a silent move into another work area hid what had happened. The
        // concrete selection below still moves on, for a view that needs one work area.
        if (
          this.selectedProjectId &&
          !selectedProjectExists &&
          !this.allWorkAreas &&
          !this.selectedNamespaceId
        ) {
          if (!inAView) {
            this.unavailableWorkArea = {
              id: this.selectedProjectId,
              name: this.rememberedWorkAreaName,
            };
          }
          this.selectAllWorkAreas();
        }
        if (!selectedProjectExists && projects.length > 0) {
          // ⛔ Remembered, not chosen: a fallback work area must not end the All scope a reader chose
          // (I-0405). Measured: a window with All remembered and no selection yet opened narrowed.
          this.rememberSelection(projects[0].id);
        } else if (this.selectedProjectId) {
          // The names have just arrived: the remembered label is refreshed against them, so a work
          // area renamed in another window stops being stale here (I-0611).
          this.rememberWorkAreaName(this.selectedProjectId);
        }
        if (projects.length === 0) {
          this.clearProjectSelection();
        }
        this.hasWorkAreaAnswer = true;
        this.error = null;
        this.failure = null;
        reportWorkAreaDiscovery({ failed: false });
        // A background read leaves a foreground one's loading state to that read.
        if (!options.background) this.loading = false;
        this.lastAnsweredAt = Date.now();
      });
      // The read answered, so the tab has a session: the person's stream may open now.
      this.wakeLive?.();
    } catch (caught) {
      runInAction(() => {
        // The list that was already on screen is KEPT. Discarding it turned every transient
        // failure into "you have no work areas": the switcher emptied, the selection cleared, and
        // nothing re-fetched, so a backend restart under an open tab left the user unable to select
        // any work area at all until they reloaded the page. Three stable deploys in one evening
        // did exactly that.
        //
        // A failed fetch is not a statement about how many work areas exist, and it must not be
        // rendered as one. The error is reported alongside what we last knew, not instead of it.
        if (!options.background) this.loading = false;
        this.error = failureToSay(caught, 'navigation.actions.loadWorkAreas');
        this.failure = describeLoadFailure(caught);
        // KD-17: a work area that did not arrive is unread now, and its gate's Retry reads again.
        reportWorkAreaDiscovery({ failed: true, retry });
      });
    }
  }

  async fetchWorkAreaTypes(): Promise<void> {
    const types = await projectApi.workAreaTypes();
    runInAction(() => {
      this.workAreaTypes = types;
    });
  }

  /** The last concrete work area, recorded without changing the scope. */
  private rememberSelection(id: string): void {
    this.selectedProjectId = id;
    deviceSession.setItem(SELECTED_KEY, id);
    deviceLocal.setItem(SELECTED_KEY, id);
    this.rememberWorkAreaName(id);
  }

  /**
   * Record the selected work area's name beside its id (I-0611).
   *
   * Silent when the name is not known here yet: a remembered name that is right is worth having and
   * one that is guessed is not, and the very next `fetchProjects` records it.
   */
  private rememberWorkAreaName(id: string): void {
    const project = this.projectMap[id];
    const name = project?.name;
    if (!name) return;
    this.rememberedWorkAreaName = name;
    deviceSession.setItem(SELECTED_NAME_KEY, name);
    deviceLocal.setItem(SELECTED_NAME_KEY, name);
    const refs = [...new Set([workAreaRouteRef(project), workAreaPermalinkRef(project)])];
    // I-2503: another work area's logo says nothing about this one's.
    if (refs.join('\n') !== this.rememberedWorkAreaRefs.join('\n'))
      this.rememberWorkAreaMark(false);
    this.rememberedWorkAreaRefs = refs;
    deviceSession.setItem(SELECTED_REFS_KEY, refs.join('\n'));
    deviceLocal.setItem(SELECTED_REFS_KEY, refs.join('\n'));
  }

  /**
   * Ask the server what this work area's policy actually resolves to.
   *
   * A failure leaves the rules absent and is recorded in `effectiveRulesFailed`: this feeds a notice
   * *before* an action, and a work area whose rules could not be read must never be described as
   * requiring nothing — which is the false statement `I-0541` was filed for — but it must say that
   * the rules could not be read, so the reader is not left waiting on an answer that is not coming.
   * The server still checks the write either way.
   */
  async fetchEffectiveRules(projectId: string): Promise<boolean> {
    runInAction(() => {
      delete this.effectiveRulesFailed[projectId];
    });
    try {
      const rules = await fetchWorkAreaEffectiveRules(projectId);
      runInAction(() => {
        this.effectiveRulesMap[projectId] = rules;
        delete this.effectiveRulesFailure[projectId];
      });
      return true;
    } catch (caught) {
      runInAction(() => {
        this.effectiveRulesFailed[projectId] = true;
        this.effectiveRulesFailure[projectId] = describeLoadFailure(caught);
      });
      // Also returned, so the caller that must have them (Settings → Policy) can say so itself.
      return false;
    }
  }

  selectProject(id: string): void {
    this.selectedProjectId = id;
    void this.fetchEffectiveRules(id);
    // Choosing one work area is leaving All work areas and any namespace scope.
    this.allWorkAreas = false;
    this.selectedNamespaceId = null;
    deviceSession.setItem(SCOPE_KEY, id);
    deviceLocal.setItem(SCOPE_KEY, id);
    this.rememberedScope = id;
    deviceSession.setItem(SELECTED_KEY, id);
    // ⛔ Written on every selection, not only the first. The fallback is *the last work area this
    // browser was in*, and one that lagged behind would reopen a choice the reader had left.
    deviceLocal.setItem(SELECTED_KEY, id);
    deviceLocal.setItem(LAST_OPENED_KEY, id);
    this.lastOpenedWorkAreaId = id;
    this.rememberWorkAreaName(id);
    this.recentWorkAreaIds = [id, ...this.recentWorkAreaIds.filter((other) => other !== id)].slice(
      0,
      RECENT_LIMIT,
    );
    deviceLocal.setItem(RECENT_KEY, JSON.stringify(this.recentWorkAreaIds));
  }

  /** I-1472: what the Agents view's read found outside the person's work areas. */
  noteSessionsOutsideWorkAreas(outside: boolean): void {
    if (this.sessionsOutsideWorkAreas !== outside) this.sessionsOutsideWorkAreas = outside;
  }

  /** I-1213: the unavailable scope has been fallen back from and said; it is not said again. */
  acknowledgeUnavailableAppScope(): void {
    this.unavailableAppScope = null;
  }

  /** I-1876: the work area that went away has been said; it is not said again. */
  acknowledgeUnavailableWorkArea(): void {
    this.unavailableWorkArea = null;
  }

  /**
   * F-0530 (AC-19.1): the person chose the app's scope — in the native sheet, through a link into
   * another work area, or by Undo. Stored for every tab and the next launch, and applied to this
   * window. Returns the scope it replaced.
   *
   * ⛔ The only writer of the app scope. `enterWorkArea` and the I-1118 redirect change this window's
   * selection and never this: an address is not a choice.
   */
  chooseAppScope(scope: string): string | null {
    const previous = this.appScope;
    this.appScope = scope;
    deviceLocal.setItem(APP_SCOPE_KEY, scope);
    if (scope === ALL_WORK_AREAS) this.selectAllWorkAreas();
    else this.selectProject(scope);
    return previous;
  }

  /**
   * Scope the app to every work area (I-0405). The last concrete selection is kept, not cleared.
   *
   * ⛔ Nothing here navigates. Where the reader ends up is the caller's decision — a view that supports
   * the scope keeps them where they are.
   */
  selectAllWorkAreas({ remember = true }: { remember?: boolean } = {}): void {
    this.allWorkAreas = true;
    this.selectedNamespaceId = null;
    deviceSession.setItem(SCOPE_KEY, ALL_WORK_AREAS);
    /*
      I-1118: only a person's choice is remembered for the next window. A page that merely is about
      every work area (`/-/work-areas/*`) scopes its own window; the phone's tabs are windows that
      share this storage, and the Decisions tab opening wrote All for the Items tab, which then
      asked "Choose a work area" of a person with one work area remembered.
    */
    if (remember) {
      deviceLocal.setItem(SCOPE_KEY, ALL_WORK_AREAS);
      this.rememberedScope = ALL_WORK_AREAS;
    }
  }

  /**
   * Scope the app to one namespace's whole subtree (F-0383, §10.9) — the third scope beside a
   * single work area and All work areas, exactly one of the three at a time. The last concrete
   * work-area selection is kept, the same reason {@link selectAllWorkAreas} keeps it: a view that
   * needs one work area can still offer it back.
   *
   * ⛔ Nothing here navigates, for the same reason `selectAllWorkAreas` does not: where the reader
   * ends up is the caller's decision.
   */
  selectNamespace(namespaceId: string): void {
    this.allWorkAreas = false;
    this.selectedNamespaceId = namespaceId;
    const value = `${NAMESPACE_SCOPE_PREFIX}${namespaceId}`;
    deviceSession.setItem(SCOPE_KEY, value);
    deviceLocal.setItem(SCOPE_KEY, value);
    this.rememberedScope = value;
  }

  /**
   * A route that names a work area was opened: that work area becomes the scope, even when it is
   * already the remembered selection (the All or namespace scope would otherwise survive a link
   * into one work area).
   */
  enterWorkArea(id: string): void {
    if (this.allWorkAreas || this.selectedNamespaceId || this.selectedProjectId !== id) {
      this.selectProject(id);
    }
  }

  /**
   * I-1213 (AC-3): `userId` is signed in. Places stored for anyone else are dropped, here and in
   * storage, before anything reads them to open a screen; the owner is then this person. A store
   * with no owner yet (the first launch with this rule) is taken as this person's.
   */
  adoptPerson(userId: string): void {
    let owner: string | null = null;
    try {
      owner = deviceLocal.getItem(PLACE_OWNER_KEY);
    } catch {
      return;
    }
    if (owner === userId) return;
    if (owner !== null) {
      // F-0534 PPD-2: every person store the register puts under the owner rule (`storeRegister.ts`).
      dropOwnerRuleStoresOfThisDevice();
      // I-1244: the local-sync cache too; every read waits for the delete.
      dropOwnerRuleDatabases();
      this.selectedProjectId = null;
      this.rememberedWorkAreaName = null;
      this.rememberedWorkAreaRefs = [];
      this.allWorkAreas = false;
      this.selectedNamespaceId = null;
      this.lastOpenedWorkAreaId = null;
      this.rememberedScope = null;
      this.appScope = null;
      this.recentWorkAreaIds = [];
      this.unavailableAppScope = null;
      this.unavailableWorkArea = null;
      // I-2104: the list that answered was the last person's. Kept, an empty one drew "No work areas
      // yet" for this person until their own read arrived, and a full one drew the last person's work
      // areas. Withdrawn, the screens wait for this person's answer, which is asked for at once.
      this.projectMap = {};
      this.projectIds = [];
      this.dataSourceMap = {};
      this.unavailableDataSources = [];
      this.hasWorkAreaAnswer = false;
      this.refreshWorkAreas();
    }
    deviceLocal.setItem(PLACE_OWNER_KEY, userId);
  }

  /**
   * F-0783 (§4d WAD-9): a work area this person just deleted leaves the list at once, so the sidebar
   * and the picker drop it before any re-read. Its scope falls back to All work areas, which is
   * where the deleting page goes, and nothing says "isn't available any more" for it in this window:
   * this person did it, and was told.
   */
  forgetWorkArea(id: string): void {
    if (!this.projectMap[id] && !this.projectIds.includes(id)) return;
    const { [id]: _project, ...projectMap } = this.projectMap;
    this.projectMap = projectMap;
    this.projectIds = this.projectIds.filter((other) => other !== id);
    const { [id]: _source, ...dataSourceMap } = this.dataSourceMap;
    this.dataSourceMap = dataSourceMap;
    if (this.recentWorkAreaIds.includes(id)) {
      this.recentWorkAreaIds = this.recentWorkAreaIds.filter((other) => other !== id);
      deviceLocal.setItem(RECENT_KEY, JSON.stringify(this.recentWorkAreaIds));
    }
    if (this.lastOpenedWorkAreaId === id) {
      this.lastOpenedWorkAreaId = null;
      deviceLocal.removeItem(LAST_OPENED_KEY);
    }
    if (this.appScope === id) this.chooseAppScope(ALL_WORK_AREAS);
    if (this.selectedProjectId === id) {
      this.clearProjectSelection();
      this.selectAllWorkAreas();
      if (this.projectIds.length > 0) this.rememberSelection(this.projectIds[0]);
    }
  }

  clearProjectSelection(): void {
    this.selectedProjectId = null;
    this.rememberedWorkAreaName = null;
    this.rememberedWorkAreaRefs = [];
    deviceSession.removeItem(SELECTED_KEY);
    deviceLocal.removeItem(SELECTED_KEY);
    deviceSession.removeItem(SELECTED_NAME_KEY);
    deviceLocal.removeItem(SELECTED_NAME_KEY);
    deviceSession.removeItem(SELECTED_REFS_KEY);
    deviceLocal.removeItem(SELECTED_REFS_KEY);
    this.rememberWorkAreaMark(false);
  }

  async createProject(data: ICreateCloudWorkAreaInput): Promise<IProject> {
    const project = await this.dataSourceRegistry.createCloudWorkArea(data);
    runInAction(() => {
      this.projectMap[project.id] = project;
      const descriptor = this.dataSourceRegistry.descriptor(project.id);
      if (descriptor) {
        this.dataSourceMap = { ...this.dataSourceMap, [project.id]: descriptor };
      }
      if (!this.projectIds.includes(project.id)) {
        this.projectIds = [...this.projectIds, project.id];
      }
      this.selectProject(project.id);
    });
    return project;
  }

  async updateProject(id: string, data: IUpdateWorkAreaInput): Promise<IProject> {
    const project = await this.dataSourceRegistry
      .updateWorkArea(id, withPoliciesPrecondition(data, this.projectMap[id]))
      .catch((error: unknown) => this.refusedAsStale(id, error));
    runInAction(() => {
      this.projectMap[project.id] = project;
      const descriptor = this.dataSourceRegistry.descriptor(project.id);
      if (descriptor) {
        this.dataSourceMap = { ...this.dataSourceMap, [project.id]: descriptor };
      }
      if (!this.projectIds.includes(project.id)) {
        this.projectIds = [...this.projectIds, project.id];
      }
    });
    // Saving Settings → Workflow is exactly the act that changes what the gates resolve to, and the
    // effective policy is the server's answer rather than something derivable from the response —
    // so it is re-asked here rather than recomputed, and the close dialog stops describing the
    // policy the work area had a moment ago.
    if (data.policiesPatch !== undefined) {
      await this.fetchEffectiveRules(project.id);
    }
    return project;
  }

  /**
   * Settings → Agents' "Save activation" (I-0803): each agent through the activation endpoint, in
   * order, so each one is bound where it is made active. The work area as the last call left it is
   * what the screen reads next.
   */
  async setAgentActivations(
    id: string,
    requests: ReadonlyArray<{ agentId: string; active: boolean; expectedActive?: boolean }>,
  ): Promise<void> {
    let project: IProject | null = null;
    let done = 0;
    try {
      for (const request of requests) {
        project = await projectApiFor(id).setAgentActivation(
          id,
          request.agentId,
          request.active,
          request.expectedActive,
        );
        done += 1;
      }
    } catch (error) {
      // The calls before the refused one were saved: read the work area again, so the screen shows
      // what is stored rather than a draft that is half true.
      const current = await projectApiFor(id)
        .findById(id)
        .catch(() => null);
      if (current) {
        runInAction(() => {
          this.projectMap[id] = current;
        });
      }
      // I-1792: a switch whose activation moved meanwhile is a stale-settings refusal, which says
      // what the calls before it did store (each agent is its own write).
      const payload =
        (error as { response?: { status?: number; data?: unknown } })?.response?.status === 409
          ? (error as { response: { data?: unknown } }).response.data
          : undefined;
      const conflict = workAreaDetailsConflictOf(payload);
      if (!conflict) throw error;
      throw (await staleRefusals()).staleDetailsRefusalOf(
        conflict.changedFields,
        current !== null,
        done === 0 ? undefined : `${done} other switch(es) were saved; the rest were not.`,
      );
    }
    if (!project) return;
    const stored = project;
    runInAction(() => {
      this.projectMap[stored.id] = stored;
    });
    await this.fetchEffectiveRules(stored.id);
  }

  /**
   * A policies save refused because someone else saved first (F-0288): the work area is read again,
   * so every Settings screen resets to what is stored, and the reader is told what changed and to
   * save again. Anything else is rethrown untouched.
   *
   * The refusal carries no copy of what is stored, so everything this store holds for the work area
   * is read again before the refusal is thrown: the work area and the rules in force, which Settings
   * → Policy, the Workflow summary and the allowed moves are drawn from (I-1279, SD-11). Policy kept
   * the other writer's rule as it was before their save.
   *
   * What is thrown says what changed and whether that read worked, and no more (I-1312, SD-15):
   * "The settings now show what is stored" is the form's to say, once it does
   * (`SettingsSaveFailure`). A form that keeps the person's edit, or is still reading something of
   * its own, would be made to say it falsely.
   */
  private async refusedAsStale(id: string, error: unknown): Promise<never> {
    const payload =
      (error as { response?: { status?: number; data?: unknown } })?.response?.status === 409
        ? (error as { response: { data?: unknown } }).response.data
        : undefined;
    // I-1792: the work area's own details (title, description, …), refused because one moved.
    const detailsConflict = workAreaDetailsConflictOf(payload);
    if (detailsConflict) {
      try {
        const current = await projectApiFor(id).findById(id);
        runInAction(() => {
          this.projectMap[id] = current;
        });
      } catch {
        throw (await staleRefusals()).staleDetailsRefusalOf(detailsConflict.changedFields, false);
      }
      throw (await staleRefusals()).staleDetailsRefusalOf(detailsConflict.changedFields, true);
    }
    const conflict = workAreaPoliciesConflictOf(payload);
    if (!conflict) throw error;
    try {
      const [current, rulesRead] = await Promise.all([
        projectApiFor(id).findById(id),
        this.fetchEffectiveRules(id),
      ]);
      runInAction(() => {
        this.projectMap[id] = current;
      });
      if (!rulesRead) throw new Error('The rules in force could not be read again.');
    } catch {
      throw (await staleRefusals()).staleSettingsRefusalOf(conflict.changedFields, false);
    }
    throw (await staleRefusals()).staleSettingsRefusalOf(conflict.changedFields, true);
  }

  async createWorkAreaType(data: {
    key: string;
    name: string;
    titleI18n?: Record<string, string>;
    defaults?: IWorkAreaTypeDefaults;
    position?: number;
    isActive?: boolean;
    isDefault?: boolean;
  }): Promise<IWorkAreaType> {
    const type = await projectApi.createWorkAreaType(data);
    runInAction(() => {
      this.workAreaTypes = [...this.workAreaTypes, type].sort((a, b) => a.position - b.position);
    });
    return type;
  }

  async updateWorkAreaType(
    id: string,
    data: Partial<{
      key: string;
      name: string;
      titleI18n: Record<string, string>;
      defaults: IWorkAreaTypeDefaults;
      position: number;
      isActive: boolean;
      isDefault: boolean;
    }>,
  ): Promise<IWorkAreaType> {
    const type = await projectApi.updateWorkAreaType(id, data);
    runInAction(() => {
      this.workAreaTypes = this.workAreaTypes
        .map((current) => {
          if (current.id === id) return type;
          if (type.isDefault && current.isDefault) return { ...current, isDefault: false };
          return current;
        })
        .sort((a, b) => a.position - b.position);
    });
    return type;
  }

  async removeWorkAreaType(id: string): Promise<void> {
    await projectApi.removeWorkAreaType(id);
    runInAction(() => {
      this.workAreaTypes = this.workAreaTypes.filter((type) => type.id !== id);
    });
  }
}

/**
 * The update with the revision of the policies this browser holds, when it carries policies
 * (F-0288). The Settings screens build their policies from `projectMap[id].policies`, so that is
 * the version the save was composed from; the server refuses the save if it is no longer stored.
 * A work area this store has not loaded with its policies sends no revision, as before.
 */
export function withPoliciesPrecondition(
  data: IUpdateWorkAreaInput,
  held: IProject | undefined,
): IUpdateWorkAreaInput {
  if (data.policiesPatch === undefined || !held) return data;
  if (!('policies' in held)) return data;
  return { ...data, expectedPoliciesRevision: policiesRevisionOf(held.policies ?? null) };
}
